'use client';

import * as Tabs from '@radix-ui/react-tabs';
import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { FiPlus, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { HiDotsVertical } from 'react-icons/hi';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type TabData = {
  id: string;
  label: string;
  content: React.ReactNode;
};

type DynamicTabsProps = {
  tabs: TabData[];
  variant?: 'default' | 'pills' | 'underline';
  className?: string;
  onAddTab?: () => void;
  showAddButton?: boolean;
  showTabActions?: boolean;
  onEditTab?: (id: string) => void;
  onDeleteTab?: (id: string) => void;
  activeId?: string;
  onTabChange?: (id: string) => void;
  extraButton?: React.ReactNode;
  tabsPerPage?: number;
};

const DynamicTabs: React.FC<DynamicTabsProps> = ({
  tabs,
  variant = 'default',
  className,
  onAddTab,
  showAddButton = false,
  showTabActions = false,
  onEditTab,
  onDeleteTab,
  activeId,
  onTabChange,
  extraButton,
  tabsPerPage = 11,
}) => {
  const [internalTab, setInternalTab] = useState(tabs[0]?.id || '');
  const [currentPage, setCurrentPage] = useState(0);
  const [userChangedPage, setUserChangedPage] = useState(false);

  const currentValue = activeId ?? internalTab;
  const totalPages = Math.ceil(tabs.length / tabsPerPage);

  // Reset to first page if active tab is not in current page
  // Only do this if the user didn't manually change the page
  useEffect(() => {
    if (tabs.length > tabsPerPage && !userChangedPage) {
      const activeTabIndex = tabs.findIndex(tab => tab.id === currentValue);
      const activeTabPage = Math.floor(activeTabIndex / tabsPerPage);

      if (activeTabPage !== currentPage) {
        setCurrentPage(activeTabPage);
      }
    }
  }, [currentValue, tabs, tabsPerPage, currentPage, userChangedPage]);

  const setTab = (id: string) => {
    if (onTabChange) onTabChange(id);
    else setInternalTab(id);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setUserChangedPage(true);
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setUserChangedPage(true);
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
    setUserChangedPage(false);
  }, [tabs]);

  useEffect(() => {
    const newTotalPages = Math.ceil(tabs.length / tabsPerPage);
    if (currentPage >= newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages - 1);
    }
  }, [tabs, tabsPerPage, currentPage]);

  // Get tabs for current page
  const startIndex = currentPage * tabsPerPage;
  const endIndex = startIndex + tabsPerPage;
  const currentTabs = tabs.slice(startIndex, endIndex);

  // Apply different styles based on the variant
  const getTabStyles = (isActive: boolean) => {
    const baseStyles = "2xl:px-3 py-2 text-base font-bold transition-colors duration-200";

    switch (variant) {
      case 'pills':
        return cn(
          baseStyles,
          "rounded-md",
          isActive
            ? "bg-gradient-to-r from-green to-blue text-white"
            : "bg-widget hover:bg-lightGray text-white"
        );
      case 'underline':
        return cn(
          baseStyles,
          "border-b-2",
          isActive
            ? "border-b-green text-white"
            : "border-b-transparent text-[#919191] hover:text-white hover:border-b-lightGray"
        );
      default: // default style
        return cn(
          baseStyles,
          "rounded-t-md",
          isActive
            ? "bg-widget text-blue border-t-2 border-l-2 border-r-2 border-blue"
            : "bg-black text-white hover:bg-gray"
        );
    }
  };

  const getContentStyles = () => {
    const baseStyles = "p-4 rounded";

    switch (variant) {
      case 'pills':
        return cn(baseStyles, "bg-widget mt-2");
      case 'underline':
        return cn(baseStyles, "bg-transparent");
      default:
        return cn(baseStyles, "bg-widget border border-lightGray");
    }
  };

  return (
    <Tabs.Root
      value={currentValue}
      onValueChange={setTab}
      className={cn("w-full h-full flex flex-col", className)}
    >
      <div className="flex items-center border-b border-lightGray">
        {/* Left navigation button */}
        {tabs.length > tabsPerPage && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={goToPrevPage}
                  disabled={currentPage === 0}
                  className="p-1 text-white hover:text-blue transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FiChevronLeft size={18} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={5}>
                <p>Previous page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        <Tabs.List className="flex flex-1 items-center overflow-hidden">
          {currentTabs.map((tab) => (
            <Tabs.Trigger
              key={tab.id}
              value={tab.id}
              className={getTabStyles(currentValue === tab.id)}
            >
              <div className="flex items-center justify-center gap-1">
                <div className="w-4" />
                <span>{tab.label}</span>
                <div className="w-4 flex justify-center">
                  {showTabActions && (
                    <Dropdown.Root>
                      <Dropdown.Trigger asChild>
                        <button
                          className="p-1 hover:bg-gray/20 rounded"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <HiDotsVertical size={14} />
                        </button>
                      </Dropdown.Trigger>
                      <Dropdown.Content sideOffset={4} className="z-50 bg-[#1B1B1B] border border-lightGray rounded-md p-1 text-sm text-white">
                        <Dropdown.Item
                          className="px-3 py-1 rounded hover:bg-lightGray cursor-pointer"
                          onSelect={() => onEditTab && onEditTab(tab.id)}
                        >Edit</Dropdown.Item>
                        <Dropdown.Item
                          className="px-3 py-1 rounded hover:bg-lightGray cursor-pointer"
                          onSelect={() => onDeleteTab && onDeleteTab(tab.id)}
                        >Delete</Dropdown.Item>
                      </Dropdown.Content>
                    </Dropdown.Root>
                  )}
                </div>
              </div>
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        {/* Right navigation button */}
        {tabs.length > tabsPerPage && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages - 1}
                  className="p-1 text-white hover:text-blue transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FiChevronRight size={18} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={5}>
                <p>Next page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {showAddButton && onAddTab && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onAddTab}
                  className="p-1 text-white hover:text-blue transition-colors ml-2"
                >
                  <FiPlus size={20} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={5}>
                <p>Add new tab</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {extraButton}
      </div>

      {tabs.map((tab) => (
        <Tabs.Content
          key={tab.id}
          value={tab.id}
          className={cn(getContentStyles(), "flex-1 h-full overflow-hidden")}
          style={{
            padding: "0px",
          }}
        >
          {tab.content}
        </Tabs.Content>
      ))}
    </Tabs.Root>
  );
};

export default DynamicTabs;